/**
 * MobileMoreSheet — full-height bottom sheet exposing every sidebar
 * destination that didn't make the bottom-nav cut.
 *
 * Phase 1 (Figma migration) — two configurations coexist, selected at
 * render time by `getUiVariant()` from `lib/uiVariant.js`:
 *
 *   * LEGACY (default in Production): mirrors the pre-migration desktop
 *     sidebar. Unchanged from prior behavior EXCEPT Financial Independence
 *     is removed per Amendment 4 (explicit user instruction: FI must not be
 *     surfaced in navigation, Plan Ahead, mobile sheets, or dashboard
 *     actions). The route stays live at /fi for backward compatibility.
 *
 *   * NEW  (Preview / opt-in): mirrors the approved 7-item IA. Adds
 *     Plan Ahead. Keeps Goals / Wealth / Liabilities / Net Worth /
 *     Projections in a "More" group as a temporary safety net during
 *     migration — these will be re-homed into the redesigned Accounts
 *     (Phase 4) and Plan Ahead (Phase 6) pages before final removal.
 *
 * LEGACY-SHELL FREEZE (Amendment 11, 2026-07-14)
 * ----------------------------------------------
 * After Phase 1 QA closure `LEGACY_MORE_GROUPS` and every legacy-only code
 * path in this file is FROZEN. Do not modify except to remediate a
 * confirmed Production regression. All subsequent migration work targets
 * the NEW variant only.
 */
import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Receipt, Repeat, Compass,
  Wallet, Coins, FileWarning, Scale, TrendingUp,
  Lightbulb, BookOpen, Settings as SettingsIcon, Target, ChevronRight, Upload,
} from 'lucide-react';
import { getUiVariant } from '@/lib/uiVariant';

// Mirrors NAV_GROUPS in AppShell.js but only the items NOT already in
// the bottom nav (Dashboard, Transactions, Recurring). We expose Goals
// here too since it's in the Daily group on desktop but not on the
// 4-tab mobile bar.
//
// Amendment 4: Financial Independence removed from every UI surface.
const LEGACY_MORE_GROUPS = [
  {
    label: 'Daily',
    items: [
      { to: '/goals',       label: 'Goals',       icon: Target },
    ],
  },
  {
    label: 'Money',
    items: [
      { to: '/accounts',    label: 'Accounts',    icon: Wallet },
      { to: '/accounts?tab=investments', label: 'Wealth', icon: Coins },
      { to: '/accounts?tab=liabilities', label: 'Liabilities', icon: FileWarning },
    ],
  },
  {
    label: 'Long view',
    items: [
      { to: '/projections', label: 'Projections', icon: TrendingUp },
      // /fi intentionally omitted per Amendment 4. Route stays live at URL.
    ],
  },
  {
    label: 'Awareness',
    items: [
      { to: '/insights',    label: 'Insights',    icon: Lightbulb },
      { to: '/reflections', label: 'Reflections', icon: BookOpen },
    ],
  },
  {
    label: 'You',
    items: [
      { to: '/settings',    label: 'Settings',    icon: SettingsIcon },
    ],
  },
];

// New IA (Amendment 4, refined 2026-07-14). Mirrors the desktop primary
// + secondary nav exactly — one mental map across form factors. The
// legacy backward-compat "More" group (Goals / Wealth / Liabilities /
// Net Worth / Projections) has been REMOVED because those pages will be
// re-homed inside Plan Ahead (Goals, Projections) and Accounts (Wealth,
// Liabilities, Net Worth) in the redesigned experience. Direct URLs\n// remain live for backward compatibility.
const NEW_MORE_GROUPS = [
  {
    label: 'Primary',
    items: [
      { to: '/dashboard',    label: 'Overview',     icon: LayoutDashboard },
      { to: '/transactions', label: 'Transactions', icon: Receipt },
      { to: '/accounts',     label: 'Accounts',     icon: Wallet },
      { to: '/recurring',    label: 'Recurring',    icon: Repeat },
      { to: '/plan-ahead',   label: 'Plan Ahead',   icon: Compass },
      { to: '/insights',     label: 'Insights',     icon: Lightbulb },
      { to: '/reflections',  label: 'Reflections',  icon: BookOpen },
    ],
  },
  {
    label: 'Actions',
    items: [
      { to: '/import',   label: 'Import Statement', icon: Upload },
      { to: '/settings', label: 'Settings',         icon: SettingsIcon },
    ],
  },
];

export default function MobileMoreSheet({ open, onOpenChange }) {
  const navigate = useNavigate();
  const location = useLocation();

  const uiVariant = getUiVariant();
  const groups = uiVariant === 'new' ? NEW_MORE_GROUPS : LEGACY_MORE_GROUPS;

  const go = (to) => {
    onOpenChange?.(false);
    if (location.pathname !== to) navigate(to);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-3xl border-t border-border/70 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 max-h-[85vh] overflow-y-auto"
        data-testid="mobile-more-sheet"
      >
        <SheetHeader className="text-left mb-2">
          <SheetTitle className="text-base">All sections</SheetTitle>
        </SheetHeader>
        <div className="space-y-5 pb-3">
          {groups.map((group) => (
            <div key={group.label}>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground px-1 mb-1.5">
                {group.label}
              </div>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = location.pathname.startsWith(item.to);
                  return (
                    <button
                      type="button"
                      key={item.to}
                      onClick={() => go(item.to)}
                      className={
                        'w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ' +
                        'border border-transparent hover:bg-muted active:bg-muted/80 ' +
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ' +
                        (active ? 'bg-primary/[0.06] border-primary/30' : '')
                      }
                      data-testid={`mobile-more-${item.to.replace(/\//g, '-').replace(/^-/, '')}`}
                    >
                      <div className={'h-9 w-9 rounded-lg grid place-items-center shrink-0 ' + (active ? 'bg-primary/15 text-primary' : 'bg-muted text-foreground/80')}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className={'text-sm flex-1 ' + (active ? 'font-semibold text-primary' : 'font-medium')}>
                        {item.label}
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
